import PgButton from '../utils/pg-button';

const { registerBlockType } = wp.blocks;
const {
  RichText,
  MediaUpload,
  MediaUploadCheck,
  InnerBlocks,
  InspectorControls,
  URLInputButton,
  ColorPalette,
} = wp.blockEditor;
const { Button, PanelBody, SelectControl, ToggleControl, Icon } = wp.components;
const { Fragment } = wp.element;
const { __ } = wp.i18n;

registerBlockType('pg/top-with-image', {
  title: __('PG Top With Image'),
  icon: 'format-image',
  category: 'layout',
  attributes: {
    eyebrow: { type: 'string', default: '' },
    heading: { type: 'string', default: '' },
    imgSrc: {
      type: 'string',
      default: '',
    },
    imgAlt: {
      // Added imgAlt for alt text
      type: 'string',
      default: '',
    },
    buttons: {
      type: 'array',
      default: [],
      source: 'query',
      selector: '.buttons a',
      query: {
        link: {
          type: 'string',
          source: 'attribute',
          attribute: 'href',
        },
        label: {
          type: 'string',
          source: 'text',
        },
        buttonClass: {
          type: 'string',
          source: 'attribute',
          attribute: 'class',
        },
        buttonColor: {
          type: 'string',
          default: '',
          source: 'attribute',
          attribute: 'data-color',
        },
      },
    },
    orientation: { type: 'string', default: 'image-on-left' },
    layout: { type: 'string', default: 'default' },
    sectionClass: { type: 'string', default: 'section-light-primary' },
  },
  edit: ({ attributes, setAttributes }) => {
    const {
      eyebrow,
      heading,
      imgSrc,
      imgAlt,
      buttons,
      orientation,
      layout,
      sectionClass,
    } = attributes;

    const onSelectImage = (media) => {
      setAttributes({ imgSrc: media.url, imgAlt: media.alt || '' }); // Set imgSrc and imgAlt
    };

    const onChangeOrientation = (newOrientation) => {
      setAttributes({ orientation: newOrientation });
    };

    const onChangeSectionClass = (newClass) => {
      setAttributes({ sectionClass: newClass });
    };

    const onChangeLayout = (newLayout) => {
      setAttributes({ layout: newLayout });
    };

    const addButton = () => {
      const newButtons = [
        ...buttons,
        {
          link: '#',
          label: 'New Button',
          buttonClass: 'btn btn-dark btn-solid',
        },
      ];
      setAttributes({ buttons: newButtons });
    };

    const removeButton = (index) => {
      const newButtons = buttons.filter((_, i) => i !== index);
      setAttributes({ buttons: newButtons });
    };

    const updateButton = (index, updates) => {
      const newButtons = buttons.map((button, i) => {
        if (i === index) {
          return { ...button, ...updates };
        }
        return button;
      });
      setAttributes({ buttons: newButtons });
    };

    return (
      <Fragment>
        <InspectorControls>
          <PanelBody title={__('Settings')}>
            <SelectControl
              label={__('Image Orientation')}
              value={orientation}
              options={[
                { label: __('Left'), value: 'image-on-left' },
                { label: __('Right'), value: 'image-on-right' },
              ]}
              onChange={onChangeOrientation}
            />
            <SelectControl
              label={__('Section Class')}
              value={sectionClass}
              options={[
                { label: 'Dark Secondary', value: 'section-dark-secondary' },
                { label: 'Dark Primary', value: 'section-dark-primary' },
                { label: 'Light Primary', value: 'section-light-primary' },
                { label: 'Light Secondary', value: 'section-light-secondary' },
              ]}
              onChange={onChangeSectionClass}
            />
            <SelectControl
              label={__('Layout')}
              value={layout}
              options={[
                { label: __('Default'), value: 'default' },
                { label: __('Custom'), value: 'custom' },
              ]}
              onChange={onChangeLayout}
            />
          </PanelBody>
        </InspectorControls>
        <section className={sectionClass}>
          <div
            className={`hero-section-wrapper hero-section-wrapper-${layout}`}
          >
            <div className={`content-container ${orientation}`}>
              <div className={`content-wrapper`}>
                <RichText
                  tagName="p"
                  className="eyebrow"
                  value={eyebrow}
                  onChange={(value) => setAttributes({ eyebrow: value })}
                  placeholder={__('Eyebrow')}
                />
                <RichText
                  tagName="h1"
                  value={heading}
                  onChange={(value) => setAttributes({ heading: value })}
                  placeholder={__('Heading')}
                />
                <div className="description">
                  <InnerBlocks
                    allowedBlocks={[
                      'core/paragraph',
                      'core/image',
                      'core/heading',
                      'core/list',
                      'core/embed',
                      'core/table',
                      'core/freeform',
                      'core/quote',
                      'pg/button',
                      'pg/banner',
                      'pg/list',
                    ]}
                    template={[
                      [
                        'core/paragraph',
                        {
                          placeholder:
                            'Start writing or type / to choose a block',
                        },
                      ],
                    ]}
                  />
                </div>
                <div className="buttons">
                  {buttons.map((button, index) => (
                    <div key={`btnIdx-${index}`}>
                      <PgButton
                        link={button.link}
                        label={button.label}
                        buttonClass={button.buttonClass}
                        additionalClass="pg-banner--button"
                        onUpdate={(object) =>
                          updateButton(index, { ...attributes, ...object })
                        }
                        onRemove={() => removeButton(index)}
                        style={
                          button.buttonColor
                            ? { color: button.buttonColor }
                            : {}
                        }
                        linkField="link"
                        labelField="label"
                        buttonClassField="buttonClass"
                        buttonTextColorField="buttonColor"
                        buttonTextColor={button.buttonColor || ''}
                      />
                    </div>
                  ))}
                  <Button isPrimary onClick={addButton}>
                    + Add Button
                  </Button>
                </div>
              </div>
              <div className="image-wrapper">
                <MediaUploadCheck>
                  <MediaUpload
                    onSelect={onSelectImage}
                    allowedTypes={['image']}
                    value={imgSrc}
                    render={({ open }) => (
                      <Button onClick={open} isPrimary>
                        {__('Upload Image')}
                      </Button>
                    )}
                  />
                </MediaUploadCheck>
                {imgSrc && <img src={imgSrc} alt={imgAlt || ''} />}{' '}
                {/* Use alt attribute */}
              </div>
            </div>
          </div>
        </section>
      </Fragment>
    );
  },
  save: ({ attributes }) => {
    const {
      eyebrow,
      heading,
      imgSrc,
      imgAlt,
      buttons,
      orientation,
      layout,
      sectionClass,
    } = attributes;

    return (
      <section className={sectionClass}>
        <div className={`hero-section-wrapper hero-section-wrapper-${layout}`}>
          <div className="content-container">
            <div className={`content-wrapper ${orientation}`}>
              <RichText.Content
                tagName="p"
                className={'eyebrow'}
                value={eyebrow}
              />
              <RichText.Content tagName="h1" value={heading} />
              <div className="description">
                <InnerBlocks.Content />
              </div>
              <p className="buttons">
                {buttons.map((button, index) => {
                  const style = button.buttonColor
                    ? { style: { color: button.buttonColor } }
                    : {};

                  return (
                    <a
                      key={index}
                      href={button.link}
                      className={button.buttonClass}
                      data-color={button.buttonColor}
                      {...style}
                    >
                      {button.label}
                    </a>
                  );
                })}
              </p>
            </div>
            {imgSrc && (
              <div className="image-wrapper">
                <img src={imgSrc} alt={imgAlt || ''} />
              </div>
            )}
          </div>
        </div>
      </section>
    );
  },
});
